// The standard-work library: every ISW, searchable, and the card viewer.
//
// The cards come from a separate app (isw.labs.interpod.app) through this
// app's BFF — see server/src/core/isw.ts for why they are proxied rather than
// linked. Nothing here knows that: it reads /api/isw/* like any other endpoint
// and frames a card served from this origin.
//
// Reading is open to everyone with a session, in every format. Nothing here
// turns on HOW someone signed in.

import { useMemo, useState } from 'react';
import { iswCardUrl } from '@/api/client';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import { useIswRecord, useIswRegister } from '@/queries';
import { useStore } from '@/store';
import type { IswFormat, IswLang, IswRegisterRow, IswStatus } from '@/api/types';

/**
 * The card languages, and the labels the design uses for them.
 *
 * Four, where the app's own chrome has three. That is not an oversight: this
 * picks the language of the CARD, which the library renders independently, and
 * a Vietnamese card exists whether or not this app's buttons are translated.
 */
const LANGS: ReadonlyArray<{ code: IswLang; label: string }> = [
  { code: 'en', label: 'AU' },
  { code: 'tl', label: 'Fil' },
  { code: 'zh', label: '中文' },
  { code: 'vi', label: 'VI' },
];

/** The app's UI language → the card language to open by default. */
export const LANG_FOR_UI: Record<string, IswLang> = { AU: 'en', 中文: 'zh', Fil: 'tl' };

export function IswLibraryScreen({ onBack }: { onBack: () => void }) {
  const t = useT();
  const uiLang = useStore((s) => s.language);
  const operator = useStore((s) => s.operator);
  const recentIds = useStore((s) => s.iswRecent);
  const noteOpened = useStore((s) => s.noteIswOpened);

  const [query, setQuery] = useState('');
  const [menuId, setMenuId] = useState<string | null>(null);
  // WHICH card is open lives in the store, because the URL names it and a
  // reload has to come back to it. Which FORMAT is local: it is a choice made
  // inside this screen and resets to the mobile card, which is what a row
  // opens anyway.
  const openIswId = useStore((s) => s.openIswId);
  const openIsw = useStore((s) => s.openIsw);
  const [openFormat, setOpenFormat] = useState<IswFormat>('mobile');

  const register = useIswRegister();
  const rows = useMemo(() => register.data?.rows ?? [], [register.data]);

  const q = query.trim();
  const matched = useMemo(() => rows.filter((r) => matches(r, q)), [rows, q]);

  // Only when not searching — a filtered list and a "recently opened" list on
  // top of each other read as one list.
  const recent = useMemo(() => {
    if (q) return [];
    return recentIds.map((id) => rows.find((r) => r.id === id)).filter((r): r is IswRegisterRow => !!r);
  }, [q, recentIds, rows]);

  const openCard = (id: string, format: IswFormat) => {
    noteOpened(id);
    setMenuId(null);
    setOpenFormat(format);
    openIsw(id);
  };

  const menuRow = menuId ? rows.find((r) => r.id === menuId) : null;

  // Every ISW in the library is a draft today, and the banner says so. It is
  // conditional rather than constant because it stops being true the day QA
  // approves one, and a standing warning that is no longer accurate is worse
  // than none — the operator learns to read past it.
  const allDraft = rows.length > 0 && rows.every((r) => r.status === 'draft');

  // Searching: how many matched. Idle: how many there are, whether they are all
  // still draft, and which language a card will open in — the three things an
  // operator would otherwise have to open one to find out.
  const tally = q
    ? `${matched.length} ${matched.length === 1 ? t.iswOneMatch : t.iswManyMatch} “${q}”`
    : [`${rows.length} ${t.iswCardsCount}`, allDraft ? t.iswAllDraftShort : null, uiLang]
        .filter(Boolean)
        .join(' · ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, animation: 'ipFade .2s' }}>
      <header style={{ flex: 'none', padding: '10px 18px 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          className="press-row"
          onClick={onBack}
          aria-label={t.back}
          style={{
            width: 40,
            height: 40,
            flex: 'none',
            borderRadius: 12,
            background: 'var(--surface2)',
            color: 'var(--ink)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon name="back" size={17} strokeWidth={2.2} />
        </button>
        <h1 style={{ font: "700 22px 'Space Grotesk',sans-serif", margin: 0, flex: 1 }}>{t.iswTitle}</h1>
      </header>

      <div style={{ flex: 'none', padding: '10px 18px 12px' }}>
        <div style={{ position: 'relative' }}>
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 13,
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              color: 'var(--sub)',
            }}
          >
            <Icon name="search" size={17} strokeWidth={1.6} />
          </span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.iswSearch}
            aria-label={t.iswSearchLabel}
            style={{
              height: 46,
              width: '100%',
              borderRadius: 12,
              background: 'var(--surface2)',
              border: '1.5px solid var(--line)',
              padding: '0 12px 0 40px',
              font: '500 14px Manrope,sans-serif',
              color: 'var(--ink)',
              WebkitAppearance: 'none',
            }}
          />
        </div>
        <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', margin: '10px 2px 0' }}>
          {tally}
        </p>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '0 18px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {allDraft && (
          <p
            style={{
              font: '600 10.5px/1.45 Manrope,sans-serif',
              color: 'var(--danger)',
              background: 'var(--surface2)',
              borderRadius: 10,
              padding: '9px 11px',
              margin: 0,
            }}
          >
            {t.iswAllDraft}
          </p>
        )}

        {recent.length > 0 && (
          <section aria-label={t.iswRecent}>
            <div className="sectionLabel">{t.iswRecent}</div>
            <RowList rows={recent} onOpen={(id) => openCard(id, 'mobile')} onMenu={setMenuId} />
          </section>
        )}

        <section aria-label={t.iswAll}>
          <div className="sectionLabel">{q ? t.iswResults : t.iswAll}</div>

          {matched.length > 0 ? (
            <RowList rows={matched} onOpen={(id) => openCard(id, 'mobile')} onMenu={setMenuId} />
          ) : (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--sub)',
                font: '500 12.5px Manrope,sans-serif',
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: 18,
              }}
            >
              <div style={{ font: "700 14px 'Space Grotesk',sans-serif", color: 'var(--ink)', marginBottom: 6 }}>
                {register.isLoading ? '…' : t.iswNoneTitle}
              </div>
              {!register.isLoading && t.iswNoneBody}
            </div>
          )}
        </section>

        <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', textAlign: 'center', margin: 0 }}>
          {t.iswBuilt} {formatBuilt(register.data?.scannedAt)}
          {operator ? ` · ${t.iswSignedInAs} ${operator.name}` : ''}
        </p>
      </div>

      <FormatSheet
        row={menuRow ?? null}
        onClose={() => setMenuId(null)}
        onPick={(format) => menuRow && openCard(menuRow.id, format)}
      />

      {openIswId && (
        <CardViewer
          id={openIswId}
          format={openFormat}
          initialLang={LANG_FOR_UI[uiLang] ?? 'en'}
          onClose={() => openIsw(null)}
        />
      )}
    </div>
  );
}

/**
 * Multi-word AND across the number, the title, the process and the PPM it
 * replaced.
 *
 * The legacy code is in the haystack because the floor still calls jobs by
 * their PPM number — somebody types "PPM-042" as readily as "ISW-042", and a
 * library that cannot answer that looks broken to them.
 */
function matches(r: IswRegisterRow, q: string): boolean {
  if (!q) return true;
  const hay = [r.id, r.title, r.process, r.legacyId ?? ''].join(' ').toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => hay.includes(word));
}

/** The library's scan time, as the date the cards were built. */
function formatBuilt(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function RowList({
  rows,
  onOpen,
  onMenu,
}: {
  rows: IswRegisterRow[];
  onOpen: (id: string) => void;
  onMenu: (id: string) => void;
}) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: 'var(--elev)',
      }}
    >
      {rows.map((r) => (
        <div
          key={r.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 14px',
            borderBottom: '1px solid var(--surface2)',
          }}
        >
          <span style={{ color: 'var(--accent)', display: 'flex', flex: 'none' }}>
            <Icon name="file" size={19} strokeWidth={1.6} />
          </span>
          <button
            type="button"
            className="press-row"
            onClick={() => onOpen(r.id)}
            style={{
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              background: 'none',
              border: 0,
              padding: 0,
              minHeight: 44,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                font: "700 10.5px 'Space Grotesk',sans-serif",
                color: 'var(--sub)',
                letterSpacing: '.05em',
              }}
            >
              {r.id}
            </span>
            <span
              style={{
                display: 'block',
                font: '600 13.5px Manrope,sans-serif',
                color: 'var(--ink)',
                marginTop: 1,
                textWrap: 'pretty',
              }}
            >
              {r.title}
            </span>
          </button>
          <StatusPill status={r.status} />
          <button
            type="button"
            className="press-row"
            onClick={() => onMenu(r.id)}
            aria-label="Other formats"
            style={{
              width: 34,
              height: 44,
              flex: 'none',
              display: 'grid',
              placeItems: 'center',
              background: 'none',
              border: 0,
              color: 'var(--sub)',
            }}
          >
            <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          <span aria-hidden="true" style={{ color: 'var(--sub)', display: 'flex', flex: 'none' }}>
            <Icon name="chevron" size={14} />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The workflow status, as the library derives it from its sign-off pipeline.
 *
 * The design draws only the draft state, because every ISW is one today. The
 * other two are here rather than assumed away: an approved card and a draft
 * card mean opposite things at a cell, and the day the first one is approved
 * this must not still be shouting "Draft" at it.
 */
export function StatusPill({ status }: { status: IswStatus }) {
  const t = useT();
  const tone =
    status === 'approved'
      ? { background: 'var(--pos)', label: t.iswApproved }
      : status === 'review'
        ? { background: 'var(--sub)', label: t.iswReview }
        : { background: 'var(--warn)', label: t.iswDraft };
  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: 6,
        font: '700 9.5px Manrope,sans-serif',
        letterSpacing: '.03em',
        textTransform: 'uppercase',
        background: tone.background,
        color: '#fff',
        flex: 'none',
      }}
    >
      {tone.label}
    </span>
  );
}

function FormatSheet({
  row,
  onClose,
  onPick,
}: {
  row: IswRegisterRow | null;
  onClose: () => void;
  onPick: (format: IswFormat) => void;
}) {
  const t = useT();
  return (
    <Sheet open={!!row} onClose={onClose} title={row?.id ?? t.iswFormats}>
      <p style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 12px' }}>
        {row ? `${row.title} · v${row.version}` : ''}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <FormatRow
          icon="pod"
          label={t.iswMobile}
          sub={t.iswMobileSub}
          selected
          onClick={() => onPick('mobile')}
        />
        <FormatRow icon="file" label={t.iswA4} sub={t.iswA4Sub} onClick={() => onPick('a4')} />
        <FormatRow icon="grid" label={t.iswStrip} sub={t.iswStripSub} onClick={() => onPick('strip')} />
        <FormatRow icon="file" label={t.iswA3} sub={t.iswA3Sub} tone="danger" onClick={() => onPick('a3')} />
      </div>
      <p style={{ font: '500 11px/1.45 Manrope,sans-serif', color: 'var(--sub)', margin: '14px 0 0' }}>
        {t.iswA3Note}
      </p>
    </Sheet>
  );
}

function FormatRow({
  icon,
  label,
  sub,
  selected = false,
  tone = 'accent',
  onClick,
}: {
  icon: 'pod' | 'file' | 'grid';
  label: string;
  sub: string;
  selected?: boolean;
  tone?: 'accent' | 'danger';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 14px',
        borderRadius: 13,
        background: selected ? 'var(--soft)' : 'var(--surface2)',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'transparent'}`,
        width: '100%',
        textAlign: 'left',
      }}
    >
      <span style={{ color: tone === 'danger' ? 'var(--danger)' : 'var(--accent)', display: 'flex', flex: 'none' }}>
        <Icon name={icon} size={20} strokeWidth={1.6} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: '600 13.5px Manrope,sans-serif', color: 'var(--ink)' }}>
          {label}
        </span>
        <span
          style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}
        >
          {sub}
        </span>
      </span>
    </button>
  );
}

/**
 * The card, full-screen, in a sandboxed frame.
 *
 * `allow-scripts` and nothing else. The card's own inline script is what packs
 * its columns and places its callouts, so without it the sheet renders blank —
 * but the sandbox withholds `allow-same-origin`, so the document runs in an
 * opaque origin with no storage and no reach back into this app, even though
 * it is served from this origin. The BFF's own response headers are the other
 * half of that: see server/src/http/isw.ts.
 *
 * Exported for the Plans & documents sheet, which opens a card over the pod
 * rather than leaving it: leaving the job screen forgets which pod it was on.
 * Mounted from inside a sheet it must be portalled out — see PodDocs.
 */
export function CardViewer({
  id,
  format,
  initialLang,
  onClose,
}: {
  id: string;
  format: IswFormat;
  initialLang: IswLang;
  onClose: () => void;
}) {
  const t = useT();
  const [lang, setLang] = useState<IswLang>(initialLang);
  const record = useIswRecord(id, lang);

  const render = record.data?.renders?.[format] ?? null;
  // The A3 is the laminated card that hangs at the cell, and the design hands
  // it to the tablet's own PDF viewer. A build that produced no PDF still has
  // the HTML, so it opens in the frame rather than doing nothing — which is
  // the state the library is actually in today.
  const pdf = format === 'a3' && render?.pdfUrl ? iswCardUrl(render.pdfUrl) : null;
  const docUrl = render ? iswCardUrl(render.url) : null;

  const title = record.data ? `${record.data.id} ${record.data.title}` : id;
  const sub = record.data ? `v${record.data.version} · ${t.iswNotIssued}` : '';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 40, display: 'flex', justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 460 }}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 41,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg)',
            animation: 'ipFade .2s',
          }}
        >
          <div
            style={{
              flex: 'none',
              height: 52,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '0 12px',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <button
              type="button"
              className="press-row"
              onClick={onClose}
              aria-label={t.dismiss}
              style={{
                width: 40,
                height: 40,
                flex: 'none',
                borderRadius: 12,
                background: 'var(--surface2)',
                color: 'var(--ink)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon name="cross" size={16} strokeWidth={3} />
            </button>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  font: "700 13px 'Space Grotesk',sans-serif",
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </span>
              <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>
                {sub}
              </span>
            </span>
            <div role="group" aria-label={t.language} style={{ display: 'flex', gap: 4, flex: 'none' }}>
              {LANGS.map((l) => {
                // Pressed follows what the library ACTUALLY rendered, not what
                // was asked for: a record with no Filipino card answers in
                // English, and lighting up Fil would be a lie about what is on
                // screen.
                const on = (render?.lang ?? lang) === l.code;
                return (
                  <button
                    key={l.code}
                    type="button"
                    className="press-row"
                    onClick={() => setLang(l.code)}
                    aria-pressed={on}
                    style={{
                      minWidth: 36,
                      height: 32,
                      padding: '0 8px',
                      borderRadius: 9,
                      border: '1px solid var(--line)',
                      background: on ? 'var(--ink)' : 'var(--surface)',
                      color: on ? 'var(--bg)' : 'var(--sub)',
                      font: '700 11px Manrope,sans-serif',
                    }}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          {pdf ? (
            // A PDF is handed to the tablet's own viewer, which is what the
            // design's external-link icon promises. The frame stays behind it.
            <PdfHandoff url={pdf} onOpened={onClose} />
          ) : docUrl ? (
            <iframe
              src={docUrl}
              title={title}
              sandbox="allow-scripts"
              style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
            />
          ) : (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24, background: 'var(--bg)' }}>
              <div
                style={{
                  maxWidth: 300,
                  textAlign: 'center',
                  color: 'var(--sub)',
                  font: '500 12.5px/1.5 Manrope,sans-serif',
                }}
              >
                <div style={{ color: 'var(--sub)', display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon name="file" size={34} strokeWidth={1.4} />
                </div>
                <div
                  style={{
                    font: "700 15px 'Space Grotesk',sans-serif",
                    color: 'var(--ink)',
                    marginBottom: 6,
                  }}
                >
                  {record.isLoading ? '…' : t.iswNoCardTitle}
                </div>
                {!record.isLoading && (record.isError ? t.iswOffline : t.iswNoCardBody)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Hands a PDF to the tablet's viewer, then closes back to the list. */
function PdfHandoff({ url, onOpened }: { url: string; onOpened: () => void }) {
  const t = useT();
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24 }}>
      <button
        type="button"
        className="press-row"
        onClick={() => {
          window.open(url, '_blank', 'noopener');
          onOpened();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '13px 16px',
          borderRadius: 13,
          background: 'var(--soft)',
          border: '1.5px solid var(--accent)',
          font: '600 13.5px Manrope,sans-serif',
          color: 'var(--ink)',
        }}
      >
        <Icon name="external" size={17} />
        {t.iswA3}
      </button>
    </div>
  );
}
